import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  Document,
  DocumentListItem,
  DocumentViewDescriptor,
  DocumentWithVersions,
  DocumentVersion,
  DocumentTaskShare,
} from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';
import { downloadFileFromUrl } from '../lib/file-download';
import { invalidateProjectRecentUpdates } from './use-project-recent-updates';

export const documentKeys = {
  list: (projectId: string, filters?: Record<string, unknown>) =>
    ['documents', projectId, filters] as const,
  detail: (documentId: string) => ['document', documentId] as const,
  taskList: (taskId: string) => ['task-documents', taskId] as const,
};

// ── Document list ────────────────────────────────────────────────────────────

export function useDocumentList(
  projectId: string,
  filters?: { search?: string; page?: number; pageSize?: number },
) {
  const queryString = new URLSearchParams(
    Object.entries(filters ?? {})
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)]),
  ).toString();

  return useQuery({
    queryKey: documentKeys.list(projectId, filters),
    queryFn: () =>
      apiClient<{
        documents: DocumentListItem[];
        pagination: { page: number; pageSize: number; total: number };
      }>(`/api/v1/projects/${projectId}/documents${queryString ? `?${queryString}` : ''}`),
    enabled: !!projectId,
  });
}

// ── Task-scoped document list ────────────────────────────────────────────────

export function useTaskDocuments(taskId: string | null | undefined) {
  return useQuery({
    queryKey: documentKeys.taskList(taskId ?? ''),
    queryFn: () =>
      apiClient<{ documents: DocumentListItem[] }>(`/api/v1/tasks/${taskId}/documents`),
    enabled: !!taskId,
  });
}

// ── Document detail ──────────────────────────────────────────────────────────

export function useDocumentDetail(documentId: string | null) {
  return useQuery({
    queryKey: documentKeys.detail(documentId ?? ''),
    queryFn: () => apiClient<{ document: DocumentWithVersions }>(`/api/v1/documents/${documentId}`),
    enabled: !!documentId,
  });
}

// ── Request upload URL ───────────────────────────────────────────────────────

export function useRequestUploadUrl(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      filename: string;
      description?: string;
      mime_type?: string;
      file_size?: number;
    }) =>
      apiClient<{ uploadUrl: string; documentId: string; versionId: string; storagePath: string }>(
        `/api/v1/projects/${projectId}/documents/upload-url`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      toast.success('Document uploaded');
    },
  });
}

// ── Confirm upload ───────────────────────────────────────────────────────────

export function useConfirmUpload(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      versionId,
      ...body
    }: {
      versionId: string;
      file_size?: number;
      mime_type: string;
    }) =>
      apiClient<{ version: DocumentVersion }>(`/api/v1/documents/versions/${versionId}/confirm`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      if (projectId) invalidateProjectRecentUpdates(queryClient, projectId);
    },
  });
}

// ── Upload new version URL ───────────────────────────────────────────────────

export function useUploadNewVersionUrl(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiClient<{ uploadUrl: string; versionId: string; storagePath: string }>(
        `/api/v1/documents/${documentId}/versions/upload-url`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      invalidateProjectRecentUpdates(queryClient, projectId);
      toast.success('New version uploaded');
    },
  });
}

// ── Mark version do_not_use ──────────────────────────────────────────────────

export function useMarkDoNotUse(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiClient<{ version: DocumentVersion }>(`/api/v1/documents/versions/${versionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'do_not_use' }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      invalidateProjectRecentUpdates(queryClient, projectId);
      toast.success('Version marked as do not use');
    },
  });
}

// ── Download URL ─────────────────────────────────────────────────────────────

export function useDownloadUrl() {
  return useMutation({
    mutationFn: (versionId: string) =>
      apiClient<{ downloadUrl: string }>(`/api/v1/documents/versions/${versionId}/download`),
  });
}

export function useDocumentView() {
  return useMutation({
    mutationFn: (versionId: string) =>
      apiClient<{ view: DocumentViewDescriptor }>(`/api/v1/documents/versions/${versionId}/view`),
  });
}

export function useDownloadDocumentVersion() {
  return useMutation({
    mutationFn: async ({ versionId, filename }: { versionId: string; filename: string }) => {
      const result = await apiClient<{ downloadUrl: string }>(
        `/api/v1/documents/versions/${versionId}/download`,
      );
      await downloadFileFromUrl(result.data.downloadUrl, filename);
    },
  });
}

// ── Share document ───────────────────────────────────────────────────────────

export function useDeleteDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId }: { documentId: string; taskIds?: string[] }) =>
      apiClient<{ success: true }>(`/api/v1/documents/${documentId}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      void queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.documentId) });
      for (const taskId of variables.taskIds ?? []) {
        void queryClient.invalidateQueries({ queryKey: documentKeys.taskList(taskId) });
        void queryClient.invalidateQueries({ queryKey: ['notes', taskId] });
      }
      invalidateProjectRecentUpdates(queryClient, projectId);
      toast.success('Document deleted');
    },
  });
}

export function useRenameDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      filename,
      version,
    }: {
      documentId: string;
      filename: string;
      version: number;
    }) =>
      apiClient<{ document: Document }>(`/api/v1/documents/${documentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ filename, version }),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      void queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.documentId) });
      invalidateProjectRecentUpdates(queryClient, projectId);
      toast.success('Document renamed');
    },
  });
}

export function useShareDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, taskId }: { documentId: string; taskId: string }) =>
      apiClient<{ share: DocumentTaskShare }>(`/api/v1/documents/${documentId}/share`, {
        method: 'POST',
        body: JSON.stringify({ task_id: taskId }),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      void queryClient.invalidateQueries({ queryKey: documentKeys.taskList(variables.taskId) });
      invalidateProjectRecentUpdates(queryClient, projectId);
      toast.success('Document shared');
    },
  });
}

// ── Unshare document ─────────────────────────────────────────────────────────

export function useUnshareDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, taskId }: { documentId: string; taskId: string }) =>
      apiClient(`/api/v1/documents/${documentId}/share/${taskId}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['documents', projectId] });
      void queryClient.invalidateQueries({ queryKey: documentKeys.taskList(variables.taskId) });
      invalidateProjectRecentUpdates(queryClient, projectId);
      toast.success('Document unshared');
    },
  });
}
